/**
 * Update Indicators Layer - Pulse Animations for Real-Time Updates
 *
 * Provides visual feedback for recently updated sensors on the map.
 * Shows pulse animations for sensors that have been updated in the last 5 seconds.
 *
 * Phase 4: Real-Time Data Updates Implementation
 * Created: January 2026
 *
 * Features:
 * - Pulse animation circles for recently updated sensors
 * - Fade-out animation over 5 seconds
 * - Offline/stale sensor indicators
 * - Last updated timestamp display
 */

import type { SensorDataPoint, SensorState } from '../MapComponent.types';

// ============================================================================
// Layer IDs
// ============================================================================

export const UPDATE_PULSE_LAYER_ID = 'sensor-update-pulse';
export const UPDATE_PULSE_SOURCE_ID = 'sensor-update-pulse-source';
export const OFFLINE_INDICATOR_LAYER_ID = 'sensor-offline-indicator';
export const STALE_INDICATOR_LAYER_ID = 'sensor-stale-indicator';

// ============================================================================
// Animation Constants
// ============================================================================

export const PULSE_DURATION = 5000; // 5 seconds
export const PULSE_MAX_RADIUS = 25;
export const PULSE_MIN_RADIUS = 8;
export const PULSE_COLOR = '#3b82f6'; // Blue
export const OFFLINE_COLOR = '#9ca3af'; // Gray
export const STALE_COLOR = '#f59e0b'; // Amber

// ============================================================================
// Pulse Animation Layer Paint
// ============================================================================

/**
 * Generate paint properties for the pulse animation layer
 * Uses data-driven styling based on time since update
 */
export const getUpdatePulseLayerPaint = () => ({
  'circle-radius': [
    'interpolate',
    ['linear'],
    ['get', 'timeSinceUpdate'],
    0, PULSE_MAX_RADIUS,     // Just updated: large pulse
    PULSE_DURATION, PULSE_MIN_RADIUS,  // 5 seconds: normal size
  ],
  'circle-color': PULSE_COLOR,
  'circle-opacity': [
    'interpolate',
    ['linear'],
    ['get', 'timeSinceUpdate'],
    0, 0.6,           // Just updated: visible
    PULSE_DURATION, 0, // 5 seconds: invisible
  ],
  'circle-stroke-width': 2,
  'circle-stroke-color': '#ffffff',
  'circle-stroke-opacity': [
    'interpolate',
    ['linear'],
    ['get', 'timeSinceUpdate'],
    0, 0.8,
    PULSE_DURATION, 0,
  ],
});

/**
 * Generate paint properties for offline sensor indicators
 */
export const getOfflineIndicatorPaint = () => ({
  'circle-radius': 6,
  'circle-color': OFFLINE_COLOR,
  'circle-opacity': 0.8,
  'circle-stroke-width': 2,
  'circle-stroke-color': '#ef4444', // Red stroke for offline
  'circle-stroke-opacity': 0.9,
});

/**
 * Generate paint properties for stale sensor indicators
 */
export const getStaleIndicatorPaint = () => ({
  'circle-radius': 6,
  'circle-color': STALE_COLOR,
  'circle-opacity': 0.7,
  'circle-stroke-width': 1.5,
  'circle-stroke-color': '#ffffff',
  'circle-stroke-opacity': 0.8,
});

// ============================================================================
// GeoJSON Generation
// ============================================================================

export interface PulseFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
  properties: {
    id: string;
    timeSinceUpdate: number;
  };
}

export interface PulseGeoJSON {
  type: 'FeatureCollection';
  features: PulseFeature[];
}

/**
 * Convert recently updated sensors to GeoJSON for pulse layer
 * @param sensors All sensor data points
 * @param recentlyUpdated Set of recently updated sensor IDs
 * @param sensorStates Map of sensor states with last update times
 * @returns GeoJSON FeatureCollection for pulse animation layer
 */
export function convertToPulseGeoJSON(
  sensors: SensorDataPoint[],
  recentlyUpdated: Set<string>,
  sensorStates: Map<string, SensorState>
): PulseGeoJSON {
  const now = Date.now();
  const features: PulseFeature[] = [];

  sensors.forEach(sensor => {
    if (recentlyUpdated.has(sensor.id)) {
      const state = sensorStates.get(sensor.id);
      const lastSeen = state?.lastSeen || new Date();
      const timeSinceUpdate = now - lastSeen.getTime();

      // Only include if within pulse duration
      if (timeSinceUpdate <= PULSE_DURATION) {
        features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [sensor.longitude, sensor.latitude],
          },
          properties: {
            id: sensor.id,
            timeSinceUpdate,
          },
        });
      }
    }
  });

  console.log(`[updateIndicators] Generated ${features.length} pulse features`);

  return {
    type: 'FeatureCollection',
    features,
  };
}

/**
 * Convert offline sensors to GeoJSON for offline indicator layer
 * @param sensors All sensor data points
 * @param sensorStates Map of sensor states
 * @returns GeoJSON FeatureCollection for offline sensors
 */
export function convertToOfflineGeoJSON(
  sensors: SensorDataPoint[],
  sensorStates: Map<string, SensorState>
): PulseGeoJSON {
  const features: PulseFeature[] = [];

  sensors.forEach(sensor => {
    const state = sensorStates.get(sensor.id);
    if (state?.status === 'offline') {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [sensor.longitude, sensor.latitude],
        },
        properties: {
          id: sensor.id,
          timeSinceUpdate: Date.now() - (state.lastSeen?.getTime() || 0),
        },
      });
    }
  });

  return {
    type: 'FeatureCollection',
    features,
  };
}

/**
 * Convert stale sensors to GeoJSON for stale indicator layer
 * @param sensors All sensor data points
 * @param sensorStates Map of sensor states
 * @returns GeoJSON FeatureCollection for stale sensors
 */
export function convertToStaleGeoJSON(
  sensors: SensorDataPoint[],
  sensorStates: Map<string, SensorState>
): PulseGeoJSON {
  const features: PulseFeature[] = [];

  sensors.forEach(sensor => {
    const state = sensorStates.get(sensor.id);
    if (state?.status === 'stale') {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [sensor.longitude, sensor.latitude],
        },
        properties: {
          id: sensor.id,
          timeSinceUpdate: Date.now() - (state.lastSeen?.getTime() || 0),
        },
      });
    }
  });

  return {
    type: 'FeatureCollection',
    features,
  };
}

// ============================================================================
// Animation Frame Handler
// ============================================================================

/**
 * Create an animation frame updater for smooth pulse animations
 * @param updateCallback Callback to update GeoJSON source
 * @param sensors All sensor data points
 * @param recentlyUpdated Set of recently updated sensor IDs
 * @param sensorStates Map of sensor states
 * @returns Cleanup function to stop animation
 */
export function createPulseAnimationLoop(
  updateCallback: (geoJson: PulseGeoJSON) => void,
  sensors: SensorDataPoint[],
  recentlyUpdated: Set<string>,
  sensorStates: Map<string, SensorState>
): () => void {
  let animationFrameId: number | null = null;
  let lastUpdateTime = 0;

  const animate = (timestamp: number) => {
    // Throttle to ~30fps for performance
    if (timestamp - lastUpdateTime >= 33) {
      lastUpdateTime = timestamp;

      // Only update if there are recently updated sensors
      if (recentlyUpdated.size > 0) {
        const geoJson = convertToPulseGeoJSON(sensors, recentlyUpdated, sensorStates);
        updateCallback(geoJson);
      }
    }

    animationFrameId = requestAnimationFrame(animate);
  };

  animationFrameId = requestAnimationFrame(animate);

  // Return cleanup function
  return () => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
    }
  };
}

// ============================================================================
// Connection Status Indicator
// ============================================================================

export interface ConnectionStatusProps {
  status: 'connected' | 'disconnected' | 'reconnecting';
  lastUpdated: Date | null;
}

/**
 * Get connection status indicator styling
 * @param status Current connection status
 * @returns CSS classes and colors for the indicator
 */
export function getConnectionStatusStyles(status: 'connected' | 'disconnected' | 'reconnecting'): {
  dotColor: string;
  textColor: string;
  label: string;
} {
  switch (status) {
    case 'connected':
      return {
        dotColor: '#22c55e', // Green
        textColor: '#16a34a',
        label: 'Live',
      };
    case 'reconnecting':
      return {
        dotColor: '#f59e0b', // Amber
        textColor: '#d97706',
        label: 'Reconnecting...',
      };
    case 'disconnected':
    default:
      return {
        dotColor: '#ef4444', // Red
        textColor: '#dc2626',
        label: 'Disconnected',
      };
  }
}

/**
 * Format the last updated timestamp for display
 * @param lastUpdated Last update timestamp
 * @returns Formatted string like "Last updated: 5s ago"
 */
export function formatLastUpdated(lastUpdated: Date | null): string {
  if (!lastUpdated) {
    return 'Never updated';
  }

  const seconds = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);

  if (seconds < 5) {
    return 'Just now';
  } else if (seconds < 60) {
    return `${seconds}s ago`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  } else {
    const hours = Math.floor(seconds / 3600);
    return `${hours}h ago`;
  }
}

// ============================================================================
// Refresh Button Styling
// ============================================================================

export interface RefreshButtonProps {
  isRefreshing: boolean;
  isRealTimeEnabled: boolean;
  refreshInterval: number;
  onClick: () => void;
}

/**
 * Get refresh button tooltip text
 * @param isRealTimeEnabled Whether real-time mode is enabled
 * @param refreshInterval Auto-refresh interval in seconds
 * @param lastUpdated Last update timestamp
 * @returns Tooltip text for the refresh button
 */
export function getRefreshButtonTooltip(
  isRealTimeEnabled: boolean,
  refreshInterval: number,
  lastUpdated: Date | null
): string {
  let tooltip = 'Refresh data';

  if (lastUpdated) {
    tooltip += ` (${formatLastUpdated(lastUpdated)})`;
  }

  if (isRealTimeEnabled) {
    tooltip += ' - Real-time enabled';
  } else if (refreshInterval > 0) {
    tooltip += ` - Auto-refresh every ${refreshInterval}s`;
  }

  return tooltip;
}

export default {
  UPDATE_PULSE_LAYER_ID,
  UPDATE_PULSE_SOURCE_ID,
  OFFLINE_INDICATOR_LAYER_ID,
  STALE_INDICATOR_LAYER_ID,
  getUpdatePulseLayerPaint,
  getOfflineIndicatorPaint,
  getStaleIndicatorPaint,
  convertToPulseGeoJSON,
  convertToOfflineGeoJSON,
  convertToStaleGeoJSON,
  createPulseAnimationLoop,
  getConnectionStatusStyles,
  formatLastUpdated,
  getRefreshButtonTooltip,
};
